import { toRosterMemberKey } from '@sorrem-client/ui/shared/roster-member-key';
import { useState } from 'react';
import type { CrucibleMember } from '@sorrem/shared';
import type { CrucibleGatheringRosterMemberState } from '@sorrem-client/game/render-config';
import { resolveItemLooks } from '@sorrem-client/game/item-effect-look';

/** Identifies one crucible membership: an account plus which of its characters. */
export interface RosterMemberKey {
    readonly accountId: string;
    readonly characterId: string;
}

export interface CrucibleRosterUpdate {
    readonly roster: readonly CrucibleGatheringRosterMemberState[];
    readonly joinedMembers: readonly RosterMemberKey[];
    readonly leftMembers: readonly RosterMemberKey[];
}

/**
 * Assigns each currently-present member the lowest free slot index
 * (0..maxPlayers-1), reusing a member's existing slot if it already has
 * one, and freeing any slot whose member has left - mutates
 * `slotAssignments` in place. A member's ritual position is therefore
 * fixed once assigned and only reused by someone else after the original
 * occupant actually leaves, never reshuffled just because an earlier
 * member in the list left.
 */
function assignSlots(members: readonly CrucibleMember[], maxPlayers: number, slotAssignments: Map<string, number>): void {
    const currentKeys = new Set(members.map((member) => toRosterMemberKey(member)));
    for (const key of [...slotAssignments.keys()]) {
        if (!currentKeys.has(key)) {
            slotAssignments.delete(key);
        }
    }

    for (const member of members) {
        const key = toRosterMemberKey(member);
        if (slotAssignments.has(key)) {
            continue;
        }
        const usedSlots = new Set(slotAssignments.values());
        let freeSlot = 0;
        while (usedSlots.has(freeSlot) && freeSlot < maxPlayers - 1) {
            freeSlot += 1;
        }
        slotAssignments.set(key, freeSlot);
    }
}

type MemberKeyMap = ReadonlyMap<string, RosterMemberKey>;

/**
 * Pure computation of one roster update: the render-worker-facing roster
 * snapshot plus which members newly joined/left since `previousMembers`.
 * Mutates `slotAssignments` in place (see `assignSlots`) - exported
 * separately from the `useCrucibleRoster` hook so it's unit-testable
 * without React.
 */
export function resolveCrucibleRosterUpdate(
    members: readonly CrucibleMember[],
    maxPlayers: number,
    previousMembers: MemberKeyMap,
    slotAssignments: Map<string, number>,
): CrucibleRosterUpdate & { readonly nextMembers: MemberKeyMap } {
    assignSlots(members, maxPlayers, slotAssignments);

    const currentMembers = new Map<string, RosterMemberKey>(
        members.map((member) => [toRosterMemberKey(member), { accountId: member.accountId, characterId: member.characterId }]),
    );

    const joinedMembers = [...currentMembers.entries()]
        .filter(([key]) => !previousMembers.has(key))
        .map(([, value]) => value);
    const leftMembers = [...previousMembers.entries()]
        .filter(([key]) => !currentMembers.has(key))
        .map(([, value]) => value);

    const roster: CrucibleGatheringRosterMemberState[] = members.map((member) => ({
        accountId: member.accountId,
        characterId: member.characterId,
        slotIndex: slotAssignments.get(toRosterMemberKey(member)) ?? 0,
        characterModelId: member.characterModelId ?? null,
        // `CrucibleMember` keeps gear and cosmetic outfit ids in separate
        // fields (so preloading can fetch each from its own catalog
        // endpoint without guessing - see the field's own doc comment in
        // session.ts) but `attachEquipmentToCharacter`'s resolution tries
        // both the equipment and body-part catalogs for every id
        // regardless of which list it came from, so they combine safely
        // into one list here - the same approach `game.ts` already uses
        // for remote players via `listCharacterVisualItemIds`. Omitting
        // outfitItemDefinitionIds here was the actual bug behind "the
        // character's full outfit is not loaded": hair/body-customization
        // pieces were never being requested at all.
        equippedItemDefinitionIds: [...(member.equippedItemDefinitionIds ?? []), ...(member.outfitItemDefinitionIds ?? [])],
        outfitModifiers: member.outfitModifiers,
        // Only a member wielding a rolled item has looks to draw (#287).
        ...(member.equippedItemTiers && Object.keys(member.equippedItemTiers).length > 0 ? { itemLooks: resolveItemLooks(member.equippedItemTiers) } : {}),
        connectionStatus: member.status ?? 'pending',
    }));

    return { roster, joinedMembers, leftMembers, nextMembers: currentMembers };
}

const EMPTY_ROSTER_UPDATE: CrucibleRosterUpdate = { roster: [], joinedMembers: [], leftMembers: [] };

/** What the roster was last computed from, and what it gave. */
interface RosterState {
    readonly members: readonly CrucibleMember[] | null;
    readonly maxPlayers: number;
    readonly slotAssignments: ReadonlyMap<string, number>;
    readonly previousMembers: MemberKeyMap;
    readonly update: CrucibleRosterUpdate;
}

const INITIAL_ROSTER_STATE: RosterState = {
    members: null,
    maxPlayers: 0,
    slotAssignments: new Map(),
    previousMembers: new Map(),
    update: EMPTY_ROSTER_UPDATE,
};

/**
 * Tracks the Crucible gathering scene's roster across party-state updates:
 * stable per-member slot assignment (see `assignSlots`) and which members
 * newly joined/left since the last update, so the scene can fire
 * `crucible-gathering-join-event`/`crucible-gathering-leave-event` only for actual joins/leaves rather
 * than every roster refresh (e.g. an equipment change).
 *
 * Computed during render from the last state (copied, never mutated), so it
 * is ready on the render the members change in, and a second render of the
 * same update (StrictMode) reports the same joins. `members` must keep its
 * identity until it changes, or every render is an update.
 */
export function useCrucibleRoster(members: readonly CrucibleMember[], maxPlayers: number): CrucibleRosterUpdate {
    const [state, setState] = useState<RosterState>(INITIAL_ROSTER_STATE);
    if (state.members === members && state.maxPlayers === maxPlayers) {
        return state.update;
    }

    const slotAssignments = new Map(state.slotAssignments);
    const result = resolveCrucibleRosterUpdate(members, maxPlayers, state.previousMembers, slotAssignments);
    const update = { roster: result.roster, joinedMembers: result.joinedMembers, leftMembers: result.leftMembers };
    setState({ members, maxPlayers, slotAssignments, previousMembers: result.nextMembers, update });
    return update;
}
